import React, { forwardRef } from 'react';
import { useIntl } from 'umi';
import moment from 'moment';
import { Button, Table } from 'tea-component';
import { scrollable } from 'tea-component/lib/table/addons';
// @ts-ignore
import * as rbd from 'react-beautiful-dnd';
import CardCommon from '@sg/components/CardCommon';
import TableContent from '@sg/components/TableContent';
import TableForm from '@sg/components/TableForm';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import PlusIcon from '@sg/assests/svg/plus-icon.svg';
import { buildUpdateTime } from '@sg/utils/common';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { createBtnPoint, SGBuriedBtnPoint, OverviewTab } from '@/pages/strategic/utils/buriedPoint';
import { pointsColums, editPointsColums } from './pageConfig';
import { useDecisionPointsList } from './hooks/useDecisionPointsList';

interface IDecisionPointsListProps {
  selectedMonth: string | null;
  maxDataTime?: string;
  canEdit: boolean;
  isGRBPEditing: boolean;
  isNotesEditing: boolean;
  onDPTEditingChange?: (isEditing: boolean) => void;
  onSectionEditingChange?: (isEditing: boolean) => void;
}

const DecisionPointsList = forwardRef<HTMLDivElement, IDecisionPointsListProps>(({
  selectedMonth,
  maxDataTime,
  canEdit,
  isGRBPEditing,
  isNotesEditing,
  onDPTEditingChange,
  onSectionEditingChange,
}, ref) => {
  const { formatMessage } = useIntl();
  const { draggable } = Table.addons;

  const {
    DPTRef,
    tablePointRef,
    decisionPointsData,
    originalDecisionPointsData,
    decisionPointsLoading,
    isDPTEditing,
    fetchDecisionPoints,
    handleSaveDecisionPoints,
    handleDeleteRow,
    handleAddNewRow,
    handleDPTEditingChange,
    handleDecisionPointsEdit,
    clearDPTAutoSave,
    autoSaveVisible,
    hasDPTUnsavedChanges,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    handleEditingChange,
  } = useDecisionPointsList({
    selectedMonth,
    maxDataTime,
    isGRBPEditing,
    isNotesEditing,
    onDPTEditingChange,
    onSectionEditingChange,
  });

  // Decision Points form lists
  const formList = [
    {
      key: 'project',
      type: 'Select',
      hidden: true, // Hidden for future enhancement
      componentConfig: {
        button: <div className="dbi-metric-select">
          <div className="dbi-metric-select-lable">
            {formatMessage({ id: 'dbs_project' })}
          </div>
          <div className="dbi-metric-select-text">
            <div className="dbi-metric-select-value">All</div>
          </div>
        </div>,
        defaultValue: 'all',
        options: [{ value: 'all', text: 'All' }],
        size: 'm',
        searchable: true,
        matchButtonWidth: true,
        appearance: 'button',
        listHeight: 600,
        className: 'dbs-metric-select',
        boxClassName: 'dbs-metric-select-overlay',
      },
    },
  ];

  const editFormList = [
    {
      key: 'add_project',
      render: () => (
        <Button
          type='weak'
          className="dbs-add-project-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={handleAddNewRow}
        >
          <img src={PlusIcon} alt="Add" className="dbs-add-project-icon" style={{ width: 16 }} />
          {formatMessage({ id: 'dbs_add_project' })}
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* Decision Points List */}
      <div ref={ref} style={{ position: 'relative', top: '-50px' }}></div>
      <div style={{ margin: '20px 0' }}>
        <CardCommon
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              {rendTitle({ title: formatMessage({ id: 'dbs_button_decision_point_list' }), tipString: 'Data is updated by the Business Operations Management team.', tips: buildUpdateTime(selectedMonth || maxDataTime) })}
              {canEdit && !isDPTEditing && (
                <Button
                  onClick={() => {
                    // Track buried point for Decision Points List Edit button
                    const btnPoint = createBtnPoint(
                      OverviewTab,
                      'decisionPointsListEdit',
                      'Decision Points List - Edit',
                    );
                    if (btnPoint) {
                      SGBuriedBtnPoint(btnPoint);
                    }
                    handleDecisionPointsEdit();
                  }}
                  style={{ height: '30px', minWidth: '60px', fontSize: '12px', marginRight: 0 }}
                  className="dbs-gradient-btn"
                >
                  {formatMessage({ id: 'dbs_edit' })}
                </Button>
              )}
              {canEdit && isDPTEditing && (
                <div className="dbs-weekly-buttons-wrapper">
                  <Button
                    onClick={() => {
                      DPTRef.current?.handleSave();
                    }}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_confirm' })}
                  </Button>
                  <Button
                    onClick={() => {
                      // Clear auto-save on cancel
                      clearDPTAutoSave();
                      DPTRef.current?.handleCancel();
                    }}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-outline-btn"
                  >
                    {formatMessage({ id: 'dbs_cancel' })}
                  </Button>
                </div>
              )}
            </div>
          }
        >
          <div style={{ marginTop: '15px' }}>
            <TableForm formList={!isDPTEditing ? formList : editFormList}></TableForm>
          </div>

          {/* Do not remove this first (this is applying the gradient to the table bottom) */}
          {/* <div className='dbs-pub-table-points' ref={tablePointRef}>   */}
          <div className='table-editable' ref={tablePointRef}>
            <TableContent
              ref={DPTRef}
              className="dbs-pub-table-points-content dbs-commons-table dbs-chats-table"
              recordKey={(record: any) =>
                String(record.id)
              }
              datas={decisionPointsData}
              loading={decisionPointsLoading}
              columns={pointsColums(formatMessage)}
              editColumns={editPointsColums(
                formatMessage,
                handleDeleteRow,
                selectedMonth ? moment(selectedMonth).startOf('month').format('YYYY-MM-DD') : moment().startOf('month').format('YYYY-MM-DD'),
                selectedMonth ? moment(selectedMonth).endOf('month').format('YYYY-MM-DD') : moment().endOf('month').format('YYYY-MM-DD'),
              )}
              onCancelRefetch={fetchDecisionPoints}
              originalData={originalDecisionPointsData}
              addons={[
                scrollable({
                  // maxHeight: '500px',
                  // minWidth: 2000,
                  onScrollBottom: (props: any) => {
                    if (tablePointRef.current) {
                      tablePointRef.current.className = '';
                    }
                    // props.target.addEventListener('scroll', function() {
                    //     tablePointRef.current.className = 'dbs-pub-table-points';
                    // });
                    (props?.target || tablePointRef?.current?.children[0]?.children[0]?.children[1]).addEventListener('scroll', () => {
                      if (tablePointRef.current) {
                        tablePointRef.current.className = 'dbs-pub-table-points';
                      }
                    });
                  },
                }),
              ]}
              onSave={handleSaveDecisionPoints}
              editable={true}
              userPermissions={{ canEdit }}
              hideButtons={true}
              validateFields={['game_id', 'name', 'status', 'urgency', 'expected_time']}
              onEditingChange={handleDPTEditingChange}
              editingAddons={[
                draggable({
                  module: rbd,
                  onDragEnd: (result: any) => {
                    // 获取当前的 editData（包含用户的编辑）
                    const currentEditData = DPTRef.current?.getEditData?.() || [];
                    // 合并编辑数据到拖拽后的数据
                    // 创建一个 Map 用于快速查找编辑数据
                    const editDataMap = new Map();
                    currentEditData.forEach((item: any) => {
                      editDataMap.set(String(item.id), item);
                    });

                    // 合并数据
                    const mergedRecords = result.map((record: any) => {
                      const editedRecord = editDataMap.get(String(record.id));
                      if (editedRecord) {
                        // 保留用户编辑的所有字段
                        return {
                          ...record,
                          date: editedRecord.date,
                          game_id: editedRecord.game_id,
                          name: editedRecord.name,
                          status: editedRecord.status,
                          urgency: editedRecord.urgency,
                          expected_time: editedRecord.expected_time,
                          issue: editedRecord.issue,
                          bos: editedRecord.bos,
                        };
                      }
                      return record;
                    });
                    DPTRef.current?.setTableData(mergedRecords);
                    DPTRef.current?.setEditData(mergedRecords);
                  },
                }),
              ]}
              emptyContent={<div className="dbs-plain-text-content-data">
                <div>
                  {canEdit ? (
                    <>
                      No Data. Please
                      <span onClick={() => {
                        DPTRef.current?.handleEdit();
                        // 使用 setTimeout 确保 handleEdit 完成后再执行 handleAddNewRow
                        setTimeout(() => {
                          handleAddNewRow();
                        }, 0);
                      }} style={{ cursor: 'pointer', color: '#7F6FF1', marginLeft: '4px' }}>Add Project</span>
                    </>
                  ) : (
                    'No Data'
                  )}
                </div>
              </div>}
            ></TableContent>
          </div>
        </CardCommon>
      </div>

      {/* Auto-save recovery modal for Decision Points */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />

      {/* 🚀 RoutePrompt for Decision Points */}
      <RoutePrompt
        when={hasDPTUnsavedChanges}
        onOk={async () => {
          const editedData = DPTRef.current?.getEditData?.() || [];
          await handleSaveDecisionPoints(editedData);
          handleEditingChange(false);
        }}
      />
    </>
  );
});

DecisionPointsList.displayName = 'DecisionPointsList';

export default DecisionPointsList;
